<template>
  <div class="col-12 col-xl-12" v-can="'questions.display'">
    <div
      class="card mb-3 mb-xxl-5 me-xxl-4"
      style="margin-top: 4rem; overflow: visible"
    >
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100"
        >
          <h6
            class="text-white text-capitalize ps-3 pe-3"
            style="font-size: 1rem"
          >
            {{ t('questions.tableTitle') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="t('questions.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile View -->
            <div class="d-block d-xl-none">
              <div v-if="questions.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-question-circle fa-2x mb-2"></i>
                <p>{{ t('questions.noQuestions') }}</p>
              </div>

              <div
                v-for="question in normalizedQuestions"
                :key="'mobile-' + question.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 class="mb-0 text-sm">
                        {{ truncateText(question.description, 50) }}
                      </h6>
                      <p class="text-xs text-muted mb-1">
                        {{ t('questions.subject') }}: {{ question.subject.name }}
                      </p>
                      <p class="text-xs text-muted mb-1">
                        {{ t('questions.chapter') }}: {{ question.chapter_subject.chapter.name }}
                      </p>
                      <p class="text-xs text-muted mb-0">
                        {{ t('questions.type') }}: {{ question.question_type.title }}
                      </p>
                    </div>
                    <div class="d-flex flex-column">
                      <span
                        class="badge badge-sm"
                        :class="getLevelClass(question.question_level.name)"
                      >
                        {{ question.question_level.name }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <router-link
                      :to="{ name: 'questions.edit', params: { id: question.id } }"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="t('common.edit')"
                      v-can="'questions.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </router-link>
                    <button
                      @click="openDeleteDialog(question)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="t('common.delete')"
                      :disabled="isProcessing"
                      v-can="'questions.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.question') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.image') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.subject') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.chapter') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.type') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.level') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ t('questions.teacher') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="questions.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">
                    <i class="fas fa-question-circle fa-2x mb-2"></i>
                    <p>{{ t('questions.noQuestions') }}</p>
                  </td>
                </tr>

                <tr v-for="question in normalizedQuestions" :key="question.id">
                  <td>
                    <div class="d-flex px-3 py-1">
                      <div class="d-flex flex-column justify-content-center">
                        <h6 class="mb-0 text-md">
                          {{ truncateText(question.description, 100) }}
                        </h6>
                      </div>
                    </div>
                  </td>
                  <td>
                    <img
                      v-if="question.image"
                      :src="question.image"
                      :alt="t('questions.imageAlt')"
                      class="img-thumbnail mt-1"
                      style="max-width: 100px"
                    />
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">
                      {{ question.subject.name }}
                    </p>
                  </td>
                  <td>
                    <p class="text-xs font-weight-bold mb-0">
                      {{ question.chapter_subject.chapter.name }}
                    </p>
                  </td>
                  <td>
                    <p class="text-xs mb-0">{{ question.question_type.title }}</p>
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="getLevelClass(question.question_level.name)">
                      {{ question.question_level.name }}
                    </span>
                  </td>
                  <td>
                    <p class="text-xs mb-0">{{ question.teacher.username }}</p>
                  </td>
                  <td class="align-middle text-end pe-4">
                    <div class="d-inline-flex">
                      <router-link
                        :to="{ name: 'questions.edit', params: { id: question.id } }"
                        class="btn btn-link text-secondary p-1 mx-1"
                        :title="t('common.edit')"
                        v-can="'questions.update'"
                      >
                        <i class="fas fa-edit fa-sm"></i>
                      </router-link>
                      <button
                        @click="openDeleteDialog(question)"
                        class="btn btn-link text-danger p-1 mx-1"
                        :title="t('common.delete')"
                        :disabled="isProcessing"
                        v-can="'questions.delete'"
                      >
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
  questions: { type: Array, required: true, default: () => [] },
  loading: { type: Boolean, default: false },
});

const isProcessing = ref(false);
const emit = defineEmits(["delete-question"]);

const normalizedQuestions = computed(() =>
  (props.questions || []).map((q) => ({
    ...q,
    description: q?.description || t("common.notAvailable"),
    image: q?.image || null,

    class: {
      ...(q?.class || {}),
      name: q?.class?.name || t("common.notAvailable"),
    },

    subject: {
      ...(q?.subject || {}),
      name: q?.subject?.name || t("common.notAvailable"),
    },

    chapter_subject: {
      ...(q?.chapter_subject || {}),
      chapter: {
        ...(q?.chapter_subject?.chapter || {}),
        name: q?.chapter_subject?.chapter?.name || t("common.notAvailable"),
      },
    },

    question_type: {
      ...(q?.question_type || {}),
      title: q?.question_type?.title || t("common.notAvailable"),
    },

    question_level: {
      ...(q?.question_level || {}),
      name: q?.question_level?.name || t("common.notAvailable"),
    },

    teacher: {
      ...(q?.teacher || {}),
      username: q?.teacher?.username || t("common.notAvailable"),
    },
  }))
);
const openDeleteDialog = question => emit("delete-question", question);

const truncateText = (text, length) => {
  if (!text) return t('common.notAvailable');
  return text.length > length ? text.substring(0, length) + "..." : text;
};

const getLevelClass = (level) => {
  if (!level) return "bg-secondary";
  const str = String(level).toLowerCase();
  switch (str) {
    case "easy":
    case "سهل":
      return "bg-success";
    case "medium":
    case "متوسط":
      return "bg-warning";
    case "hard":
    case "صعب":
      return "bg-danger";
    case "advanced":
    case "متقدم":
      return "bg-dark";
    default:
      return "bg-secondary";
  }
};
</script>
